/**
 * Primitive design tokens — intentionally framework-agnostic.
 * These are the single source of truth for all design values.
 * Never import MUI or any UI library here.
 *
 * Based on PondLogs UX Guidelines 2026
 */
export const colorTokens = {
    // Primary Colors (Core Brand Colors)
    primary: '#003C71', // Brand Blue - navigation, headers, primary buttons
    primaryDark: '#002B52',
    primaryLight: '#1A5A9A',
    // Primary tints (10%, 25%, 50%, 75%)
    primary10: '#E6EAEF',
    primary25: '#B3BFD1',
    primary50: '#8099B8',
    primary75: '#4D73A0',
    accent: '#B7BF10', // Brand Green - positive indicators, success states
    accentDark: '#949A0C',
    // Accent tints
    accent10: '#F7F8E6',
    accent25: '#E4E7B3',
    accent50: '#DBDF88',
    accent75: '#C9CF4B',
    magenta: '#C70D6D', // Highlights, selected states, special metrics
    magentaDark: '#9F0A57',
    // Magenta tints
    magenta10: '#F9E6F0',
    magenta25: '#EEBDD6',
    magenta50: '#E38BBE',
    magenta75: '#D54D99',
    // Secondary Colors (Data Visualization & Alerts)
    secondary: '#00ACA9', // Ocean - water quality, sensor readings
    secondaryDark: '#008A87',
    secondaryLight: '#E6F7F7',
    yellow: '#FBBA00', // Warnings, thresholds approaching
    yellowLight: '#FEF3D9',
    orange: '#EB6209', // Actions required, operational alerts
    orangeLight: '#FDEEE5',
    error: '#C8102E', // Critical alerts
    errorLight: '#F9E6EA',
    errorBg: '#fff0f0', // Error container background
    errorBorder: '#f5c6cb', // Error container border
    green: '#22a000', // Active/healthy operational state (distinct from brand accent)
    // Semantic colors (retained for compatibility)
    warning: '#FBBA00', // Maps to yellow
    warningLight: '#FEF3D9',
    success: '#B7BF10', // Maps to Brand Green
    successLight: '#F7F8E6',
    info: '#00ACA9', // Maps to Ocean
    infoLight: '#E6F7F7',
    // Supporting Colors (Neutral Interface)
    white: '#FFFFFF',
    darkGrey: '#646363', // Body text, secondary labels, icons
    lightGrey: '#F4F6F8', // App background, containers
    // Inactive / muted state
    inactive: '#dcdcdc', // Inactive polygon fill (maps, status chips)
    blueGray: '#546e7a', // Inactive pond header / chip accent
    blueGrayLight: '#78909C', // Inactive pond legend (lighter shade)
    // Text
    textDark: '#1a2233', // Primary dark text — headings and data values
    // Surface backgrounds
    surfaceAlt: '#f0f4f8', // Alternate subtle surface (table footer, row summary)
    primaryHoverBg: '#f0f7ff', // Primary-tinted row hover background
    segmentBg: '#EAECF0', // Segmented control container background
    // Borders / dividers
    borderLight: '#e8edf2', // Card / section divider border
    // Primary gradient stops (hero panel)
    primaryDeep: '#002044', // Deepest brand blue — gradient start
    primaryMid: '#005090', // Mid brand blue — gradient end
    // Icon
    iconNavy: '#0A2463', // PondLogs brand icon default navy
    // Neutrals (grayscale)
    gray50: '#F4F6F8', // Updated to match Light Grey
    gray100: '#EDF2F7',
    gray200: '#E2E8F0',
    gray300: '#CBD5E0',
    gray400: '#A0AEC0',
    gray500: '#718096',
    gray600: '#646363', // Updated to match Dark Grey
    gray700: '#2D3748',
    gray800: '#1A202C',
    gray900: '#171923',
    black: '#000000',
    // Semantic aliases
    /** Input border color — used for TextField/Select outlines globally */
    inputBorder: '#dde3ea',
};
export const typographyTokens = {
    fontFamily: '"Nunito", sans-serif', // Primary font
    fontFamilySecondary: '"Aptos", sans-serif', // Secondary font
    fontFamilyMono: '"JetBrains Mono", "Fira Code", "Courier New", monospace',
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightSemiBold: 600,
    fontWeightBold: 700,
    /** px values — MUI multiplies these by theme.typography.htmlFontSize internally */
    fontSize2xl: 32,
    fontSizeXl: 24,
    fontSizeLg: 20,
    fontSizeMd: 16,
    fontSizeSm: 14,
    fontSizeXs: 12,
    /** Line height ratio: 1.1x font size for headings */
    lineHeightRatio: 1.1,
};
/** Base spacing unit in px — all spacing is a multiple of this */
export const spacingBase = 8;
export const radiiTokens = {
    none: 0,
    sm: 4,
    md: 8,
    lg: 16,
    xl: 24,
    full: 9999,
};
export const shadowTokens = {
    sm: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
    md: '0 4px 6px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.08)',
    lg: '0 10px 15px rgba(0,0,0,0.10), 0 4px 6px rgba(0,0,0,0.06)',
};
